import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { T, wsClient, quoteSubscriptionManager } from '../imports';

interface QuotesContextType {
    quotes: T.Quote[];
    setQuotes: React.Dispatch<React.SetStateAction<T.Quote[]>>;
    /**
     * Informs the Quotes system to subscribe to an instrument's quote, if not already subscribed.
     * For actual quote data, components should read from the quotes array in context directly.
     * */
    requestQuote: (symbol: string, subscriber: string) => void;
    /**
     * Informs the Quotes system that a component no longer wants to subscribe to a quote.
     * If there are no more subscribers for that instrument, it is removed from the tracked list.
     */
    releaseQuote: (symbol: string, subscriber: string) => void;
}

const QuotesContext = createContext<QuotesContextType | undefined>(undefined);

const QuoteSubscriptions = new Map<string, Set<string>>(); // Map of symbol to array of subscriber IDs

export const useQuotes = () => {
    const ctx = useContext(QuotesContext);
    if (!ctx) throw new Error('useQuotes must be used within a QuotesProvider');
    return ctx;
}

export const QuotesProvider = ({ children }: { children: ReactNode }) => {
    const [quotes, setQuotes] = useState<T.Quote[]>([]);

    const requestQuote = (symbol: string, subscriber: string): void => {

        QuoteSubscriptions.set(symbol, QuoteSubscriptions.get(symbol) || new Set());
        const subscribers = QuoteSubscriptions.get(symbol)!;
        subscribers.add(subscriber);
        // console.log(`[QuotesCtx] Subscriber "${subscriber}" requested quote for: ${symbol}. Subscribers: ${Array.from(subscribers).join(', ')}`);

        if (subscribers.size === 1) {
            // First subscriber - request from server
            // console.log(`[QuotesCtx] Subscribing to: ${symbol}`);
            quoteSubscriptionManager.addTrackedSymbol(symbol);
        }
    }
    
    const releaseQuote = (symbol: string, subscriber: string): void => {

        const subscribers = QuoteSubscriptions.get(symbol);
        if (subscribers && subscribers.has(subscriber)) {
            subscribers.delete(subscriber);
            // console.log(`[QuotesCtx] Subscriber "${subscriber}" released quote for: ${symbol}. Remaining subscribers: ${Array.from(subscribers).join(', ')}`);
            if (subscribers.size === 0) {
                // No more subscribers - remove from server
                // console.log(`[QuotesCtx] Removing quote subscription for: ${symbol}`);
                QuoteSubscriptions.delete(symbol);
                quoteSubscriptionManager.removeTrackedSymbol(symbol);
            }
        }
    }

    useEffect(() => {
        // Sync quotes when WsClient receives quote updates
        wsClient.onQuotesUpdate = (incomingQuotes) => {
            // Normalize numeric fields to ensure they're always numbers, not strings
            const normalizedQuotes = incomingQuotes.map(quote => ({
                ...quote,
                last: typeof quote.last === 'string' ? parseFloat(quote.last) : quote.last,
                bid: typeof quote.bid === 'string' ? parseFloat(quote.bid) : quote.bid,
                bidSize: typeof quote.bidSize === 'string' ? parseFloat(quote.bidSize) : quote.bidSize,
                ask: typeof quote.ask === 'string' ? parseFloat(quote.ask) : quote.ask,
                askSize: typeof quote.askSize === 'string' ? parseFloat(quote.askSize) : quote.askSize,
                volume: typeof quote.volume === 'string' ? parseFloat(quote.volume) : quote.volume,
                openInterest: typeof quote.openInterest === 'string' ? parseFloat(quote.openInterest) : quote.openInterest,
            }));
            
            // Patch quotes: merge incoming quotes with existing ones
            setQuotes(prevQuotes => {
                // Create a map of existing quotes by symbol for fast lookup
                const getSymbol = (q: T.Quote) => q.instrument.symbol;
                const quoteMap = new Map(prevQuotes.map(q => [getSymbol(q), q]));

                // Update or add incoming quotes
                normalizedQuotes.forEach(quote => {
                    const symbol = quote.instrument.symbol;
                    quoteMap.set(symbol, quote);
                });

                // Convert back to array
                return Array.from(quoteMap.values());
            });
        };
        return () => {
            wsClient.onQuotesUpdate = null;
        };
    }, []);

    return (
        <QuotesContext.Provider value={{ quotes, setQuotes, requestQuote, releaseQuote }}>
            {children}
        </QuotesContext.Provider>
    );
}